import { Box, Typography, Dialog, DialogContent, DialogContentText, DialogTitle, Divider, Button, Grid } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { dataStore } from '../Store';
import consts from '../consts';
import { usePrerequisites } from "../contexts/Prerequisites";
import logger from "../components/logger";
import Connection from "../services/Connection";
import { model as EndoSetupEquipmentModel } from "./EndoSetupEquipmentTable";
import { EndoHookupModel } from "./EndoSetupHookupTable";
import { EndoParameterModel } from "./EndoSetupParameterSetsTable";
import { EndoSetuScopespModel } from "./EndoSetupSopes";
import { RapidAERHookupModel } from "./RapidAERHookupTable";

const isHulu = () => {
	const { auth } = dataStore.getState();
	return auth?.tags?.isHulu;
}
const ParameterGrid = EndoParameterModel.createGridBase();
const HookupGrid = EndoHookupModel.createGridBase();
const EndoSetupEquipmentTable = EndoSetupEquipmentModel.createGridBase();
const EndoSetuScopesGrid = EndoSetuScopespModel.createGridBase();
const RapidAERHookupGird = RapidAERHookupModel.createGridBase();

export const EndoSetup = () => {
	let [params] = useSearchParams();
	const [{ strings }] = usePrerequisites();
	const isIdSet = params.has('id');
	const loadCountRef = useRef(0)
	const hookupGridRef = useRef();
	const repidAerHookupGridRef = useRef();
	const parameterGridRef = useRef();
	const [activeRecord, setActiveRecord] = useState(null);
	const [toggleState, setToggleState] = useState({
		showHookupGrid: false,
		showRapidAERGrid: false
	});
	const navigate = useNavigate();
	const { auth } = dataStore.getState();
	const handleEndoSetupScopesLoaded = () => {
		if (loadCountRef.current > 0) {
			[hookupGridRef, parameterGridRef, repidAerHookupGridRef].forEach(ref => {
				ref.current?.refresh(false);
			});
		}
		loadCountRef.current++;
	}
	const handleOnOpenForm = ({ record }) => {
		setActiveRecord(record);
	};
	const data = useMemo(() => {
		let str = "";
		if (activeRecord?.ParameterSetValue) {
			const parameterSetVal = JSON.parse(activeRecord?.ParameterSetValue);
			str = JSON.stringify(parameterSetVal, null, 2);
		}
		else {
			str = activeRecord?.ParmSet
		}
		return str?.replace(/></g, '>\n<');
	}, [activeRecord?.ParameterSetValue, activeRecord?.ParmSet]);


	// Function to check hookup and rapidAER grid visibility
	const checkGridVisibility = async () => {
		try {
			const formValue = new FormData();
			formValue.append('otherAction', 'checkGridVisibility');
			formValue.append('action', 'otherAction');
			const response = await fetch(`${Connection.EndoSetupHookup}`, {
				method: 'POST',
				body: formValue
			});


			const result = await response.json();
			if (!result.success) {
				return;
			}
			// Update toggle states based on API response
			setToggleState(prev => ({
				...prev,
				showHookupGrid: result.data.showHookupGrid,
				showRapidAERGrid: result.data.showRapidAERGrid
			}));

		} catch (error) {
			logger.error("Error fetching hookup and rapidAER grid visibility:", error);
		}
	}
	useEffect(() => {
		checkGridVisibility();
	}, []);
	return (
		<Box>
			<Grid container>
				{!isIdSet &&
					<Grid size={12} margin={1}>
						<Typography variant="h5" color="white" gutterBottom>{strings.EquipmentForm}</Typography>
						{/* setting onOpenForm prop of EquipmentGrid to an empty object, it might cause errors if EquipmentGrid is expecting a function. It should be replaced with a proper handler function or removed if it's not necessary. */}
						<EndoSetupEquipmentTable parentFilters={{ filter: [{ "operator": "$eq", "property": "DataType", "value": consts.ModelDataType.Endo }], asArray: 0 }}
						/>
					</Grid>
				}
				<Grid size={12} margin={1}>
					<Typography variant="h5" color="white" gutterBottom>{strings.Scopes}</Typography>
					<EndoSetuScopesGrid
						onGridDataLoad={handleEndoSetupScopesLoaded}
						parentFilters={{
							filter: [{ "operator": "$eq", "property": "ItemType", "value": "Scope" }]
						}} onOpenForm={({ record, id }) => {
							if (!id) {
								navigate('/quicksearchscope');
							} else if (auth?.tags?.RoleName !== consts.RoleName.Operator) {
								navigate(`/itemsync?id=${record._id}&returnTo=/endoSetup`)
							}
						}} readOnly={false}
					/>
				</Grid>
				{isHulu() && !isIdSet && toggleState.showHookupGrid &&
					<Grid size={12} margin={1}>
						<Typography variant="h5" color="white" gutterBottom>{strings.Hookup}</Typography>
						<HookupGrid ref={hookupGridRef} disablePreferences={!isHulu()} />
					</Grid>
				}
				{toggleState.showRapidAERGrid && <Grid size={12} margin={1}>
					<Typography variant="h5" color="white" gutterBottom>{`${strings.Hookup} RapidAER`}</Typography>
					<RapidAERHookupGird  ref={repidAerHookupGridRef} onOpenForm={ ({ record }) => { navigate(`/hookupsync?type=${encodeURIComponent('hookup')}&value=${encodeURIComponent(record.HookupValue)}`) }}/>
				</Grid>}
				{isHulu() && !isIdSet &&
					<Grid size={12} margin={1}>
						<Typography variant="h5" color="white" gutterBottom>{strings.ParameterSet}</Typography>
						<ParameterGrid ref={parameterGridRef} disablePreferences={!isHulu()} onOpenForm={handleOnOpenForm} />
						<Dialog
							fullWidth
							maxWidth="md"
							open={!!activeRecord}
							sx={{
								zIndex: theme => theme.zIndex.tooltip,
								'& .MuiDialogContentText-root': {
									border: (theme) => `1px solid ${theme.palette.divider}`,
									borderRadius: (theme) => theme.spacing(0.5),
									backgroundColor: 'action.selected',
									padding: (theme) => theme.spacing(2),

								}
							}}
							disableEscapeKeyDown
						>
							<DialogTitle sx={{ display: 'flex', flexDirection: 'row' }}>
								<Typography variant="h5" color="primary" sx={{ flex: 1 }}>
									{strings.ParameterSet}
								</Typography>

								<Button
									onClick={() => setActiveRecord(null)}
									title={strings.Close}
									color="error"
									variant="contained"
									size="small"
									sx={{
										px: 1,
										fontSize: 14,
										textTransform: 'uppercase',
										fontWeight: 'bold',
									}}
								>
									{strings.Close}
								</Button>

							</DialogTitle>
							<Divider />
							<DialogContent>
								<DialogContentText>
									<pre>{data}</pre>
								</DialogContentText>
							</DialogContent>
						</Dialog>
					</Grid>
				}
			</Grid>
		</Box>

	)
}